import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

export interface ProfileMenuProps {
  userName?: string;
  userRole?: string;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  userName = 'Admin User',
  userRole = 'Project Admin',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleNavigation = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Chip
        ref={anchorRef}
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          transition: 'all .2s ease-in-out',
          bgcolor: theme.palette.primary.light,
          color: theme.palette.primary.main,
          '& .MuiChip-label': { lineHeight: 0 },
          '&:hover': {
            bgcolor: theme.palette.primary.main,
            color: '#fff',
            '& .MuiAvatar-root': { bgcolor: theme.palette.primary.dark },
          },
        }}
        icon={
          <Avatar
            sx={{
              width: 34,
              height: 34,
              margin: '8px 0 8px 8px !important',
              bgcolor: theme.palette.primary.main,
              color: '#ffffff',
            }}
          >
            <PersonOutlineIcon fontSize="small" />
          </Avatar>
        }
        label={<SettingsIcon sx={{ fontSize: '1.2rem' }} />}
        variant="outlined"
        onClick={handleToggle}
        aria-controls={open ? 'profile-menu' : undefined}
        aria-haspopup="true"
        aria-label="user-account"
      />

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 14] } }]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Grow in={open} {...TransitionProps}>
              <Paper
                elevation={16}
                sx={{
                  borderRadius: '12px',
                  minWidth: 280,
                  boxShadow: theme.shadows[16],
                }}
              >
                {open && (
                  <Box>
                    {/* User info header */}
                    <Box sx={{ p: 2, pb: 1 }}>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 44, height: 44, bgcolor: theme.palette.primary.main }}>
                          <PersonOutlineIcon />
                        </Avatar>
                        <Stack>
                          <Typography variant="h6">{userName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {userRole}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>

                    <Divider />

                    {/* Settings card */}
                    <Box sx={{ p: 2, py: 1.5 }}>
                      <Card
                        variant="outlined"
                        sx={{
                          bgcolor: theme.palette.primary.light,
                          borderColor: 'transparent',
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">
                            Role: {userRole}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>

                    <Divider />

                    {/* Menu items */}
                    <List component="nav" sx={{ p: 1 }}>
                      <ListItemButton
                        sx={{ borderRadius: '12px' }}
                        onClick={() => handleNavigation('/settings')}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ManageAccountsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography variant="body2">Account Settings</Typography>}
                        />
                      </ListItemButton>

                      <ListItemButton
                        sx={{ borderRadius: '12px' }}
                        onClick={() => handleNavigation('/settings')}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <AccountCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography variant="body2">Profile</Typography>}
                        />
                      </ListItemButton>

                      <ListItemButton
                        sx={{ borderRadius: '12px' }}
                        onClick={handleLogout}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography variant="body2">Sign Out</Typography>}
                        />
                      </ListItemButton>
                    </List>
                  </Box>
                )}
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default ProfileMenu;
